import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  LayoutDashboard,
  Palette,
  Megaphone,
  Calendar,
  Globe,
  Settings as SettingsIcon,
  ChevronDown,
  Menu as MenuIcon,
  BrainCircuit,
} from "lucide-react";

// ===========================================================================
// TopPagesMenu (Menu A) — horizontal top pages nav.
//
// A Notion/Linear-style menubar pinned below the app top bar. Each entry is a
// real route in App.jsx's `views`. Two entries carry dropdowns to real
// sub-destinations:
//   • Creative  → Posts & Ads, Performance, Analysis, Recommendations
//   • Settings  → Account, Team, Notifications, Privacy, Terms (sub-tabs)
//
// Fully independent from ChatMenu (Menu B). Opening a dropdown here never
// touches the chat panel.
// ===========================================================================

const FONT = '-apple-system, BlinkMacSystemFont, "Inter", system-ui, sans-serif';

// Colors per design system (ocean blue)
const C = {
  inactive: "#565c5e",
  active: "#275c73",
  hover: "#f7f8f8",
  border: "#dfe2e3",
  pillBg: "var(--blue-50, #eef3f5)",
  surface: "#ffffff",
  itemText: "#33383a",
};

// ── nav model — every id / sub maps to a REAL route in App.jsx ──────────────
// `route` items navigate directly. `dropdown` items open a panel of children.
const NAV = [
  { key: "dashboard", label: "Dashboard", Icon: LayoutDashboard, route: "dashboard" },
  {
    key: "creative",
    label: "Creative",
    Icon: Palette,
    children: [
      { label: "Posts & Ads", route: "creative-studio" },
      { label: "Performance", route: "performance" },
      { label: "Analysis", route: "analysis" },
      { label: "Recommendations", route: "recommendations" },
    ],
  },
  { key: "ads", label: "Ads", Icon: Megaphone, route: "ad-campaigns" },
  { key: "schedule", label: "Schedule", Icon: Calendar, route: "schedule" },
  { key: "website", label: "Website", Icon: Globe, route: "website" },
  { key: "brain", label: "Brain", Icon: BrainCircuit, route: "brain", localOnly: true },
  {
    key: "settings",
    label: "Settings",
    Icon: SettingsIcon,
    children: [
      { label: "Account", route: "settings", tab: "account" },
      { label: "Team", route: "settings", tab: "team" },
      { label: "Notifications", route: "settings", tab: "notifications" },
      { label: "Privacy", route: "settings", tab: "privacy" },
      { label: "Terms", route: "settings", tab: "terms" },
    ],
  },
];

// Which top key owns a given active route (drives the active pill highlight).
const ROUTE_OWNER = (() => {
  const m = {};
  NAV.forEach((n) => {
    if (n.route) m[n.route] = n.key;
    if (n.children) n.children.forEach((c) => { m[c.route] = n.key; });
  });
  // Disambiguate: `website` is its own top item, not Creative's.
  m["website"] = "website";
  return m;
})();

const STYLE_ID = "top-pages-menu-css";
const CSS = `
@keyframes tpm-in { from { opacity:0; transform: translateY(-6px) scale(0.98); } to { opacity:1; transform: translateY(0) scale(1); } }
@keyframes tpm-out { from { opacity:1; transform: translateY(0) scale(1); } to { opacity:0; transform: translateY(-6px) scale(0.98); } }
@keyframes tpm-item-in { from { opacity:0; transform: translateY(-4px); } to { opacity:1; transform: translateY(0); } }
.tpm-dropdown { transform-origin: top center; }
.tpm-dropdown[data-state="open"] { animation: tpm-in 200ms cubic-bezier(0.25,1.1,0.4,1); }
.tpm-dropdown[data-state="closing"] { animation: tpm-out 150ms cubic-bezier(0.25,1.1,0.4,1); }
.tpm-dropdown[data-state="open"] .tpm-item { opacity:0; animation: tpm-item-in 180ms cubic-bezier(0.25,1.1,0.4,1) forwards; }
.tpm-chevron { transition: transform 200ms cubic-bezier(0.25,1.1,0.4,1); }
.tpm-chevron[data-open="true"] { transform: rotate(180deg); }
.tpm-trigger:hover, .tpm-item:hover { background: ${C.hover} !important; }
@media (prefers-reduced-motion: reduce) {
  .tpm-dropdown[data-state="open"], .tpm-dropdown[data-state="closing"],
  .tpm-dropdown[data-state="open"] .tpm-item { animation: none !important; opacity:1 !important; transform:none !important; }
  .tpm-chevron { transition: none !important; }
}
`;

export default function TopPagesMenu({ active, setActive, goToSettingsTab, isMobile }) {
  const [openKey, setOpenKey] = useState(null);     // currently open dropdown key
  const [closingKey, setClosingKey] = useState(null); // key mid close-animation
  const [mobileOpen, setMobileOpen] = useState(false);
  const rootRef = useRef(null);
  const closeTimer = useRef(null);

  // Collapse the inline bar into a "Pages ▾" hamburger when the viewport is
  // too narrow to fit every item on one line (tablet ≤1024px and mobile).
  const [viewportW, setViewportW] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1280));
  useEffect(() => {
    const onResize = () => setViewportW(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const compact = viewportW <= 1024;

  // Inject CSS once.
  useEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const el = document.createElement("style");
    el.id = STYLE_ID;
    el.textContent = CSS;
    document.head.appendChild(el);
  }, []);

  const reduceMotion = typeof window !== "undefined" &&
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const closeDropdown = useCallback(() => {
    setOpenKey((cur) => {
      if (!cur) return null;
      if (reduceMotion) return null;
      setClosingKey(cur);
      if (closeTimer.current) clearTimeout(closeTimer.current);
      closeTimer.current = setTimeout(() => setClosingKey(null), 150);
      return null;
    });
  }, [reduceMotion]);

  const openDropdown = useCallback((key) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setClosingKey(null);
    setOpenKey(key);
  }, []);

  const toggleDropdown = useCallback((key) => {
    if (openKey === key) closeDropdown();
    else openDropdown(key);
  }, [openKey, closeDropdown, openDropdown]);

  // Close on route change.
  useEffect(() => { closeDropdown(); setMobileOpen(false); }, [active, closeDropdown]);

  // Close on outside click + Escape.
  useEffect(() => {
    if (!openKey && !mobileOpen) return;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) { closeDropdown(); setMobileOpen(false); } };
    const onKey = (e) => { if (e.key === "Escape") { closeDropdown(); setMobileOpen(false); } };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [openKey, mobileOpen, closeDropdown]);

  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);

  // Navigate to a leaf route (optionally a Settings sub-tab).
  const navigate = useCallback((item) => {
    if (item.tab && item.route === "settings" && goToSettingsTab) goToSettingsTab(item.tab);
    else setActive(item.route);
    closeDropdown();
    setMobileOpen(false);
  }, [setActive, goToSettingsTab, closeDropdown]);

  const activeKey = ROUTE_OWNER[active] || null;

  // `localOnly` items (e.g. the Brain monitor) appear ONLY on localhost — never
  // in production (vella2.com), so customers never see internal tools.
  const isLocalHost = typeof window !== "undefined" &&
    /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);
  const NAV_ITEMS = NAV.filter((n) => !n.localOnly || isLocalHost);

  // ── leaf trigger (no dropdown) ────────────────────────────────────────────
  const triggerBase = {
    display: "inline-flex", alignItems: "center", gap: 7,
    height: 34, padding: "0 12px", borderRadius: 8, border: "none",
    background: "transparent", cursor: "pointer", fontFamily: FONT,
    fontSize: 13, whiteSpace: "nowrap", transition: "background 160ms ease, color 160ms ease",
  };

  const renderTopItem = (n) => {
    const isActive = activeKey === n.key;
    const stylePill = isActive
      ? { background: C.pillBg, color: C.active, fontWeight: 500 }
      : { color: C.inactive, fontWeight: 400 };

    if (!n.children) {
      return (
        <button
          key={n.key}
          type="button"
          role="menuitem"
          aria-current={isActive ? "page" : undefined}
          className="tpm-trigger"
          data-no-vella-ring="true"
          onClick={() => navigate(n)}
          style={{ ...triggerBase, ...stylePill }}
        >
          <n.Icon size={16} strokeWidth={1.5} />
          <span>{n.label}</span>
        </button>
      );
    }

    const isOpen = openKey === n.key;
    const isClosing = closingKey === n.key;
    return (
      <div key={n.key} style={{ position: "relative" }}>
        <button
          type="button"
          role="menuitem"
          aria-haspopup="true"
          aria-expanded={isOpen}
          aria-current={isActive ? "page" : undefined}
          className="tpm-trigger"
          data-no-vella-ring="true"
          onClick={() => toggleDropdown(n.key)}
          style={{ ...triggerBase, ...stylePill }}
        >
          <n.Icon size={16} strokeWidth={1.5} />
          <span>{n.label}</span>
          <ChevronDown size={14} strokeWidth={1.5} className="tpm-chevron" data-open={isOpen ? "true" : "false"} />
        </button>
        {(isOpen || isClosing) && (
          <div
            role="menu"
            aria-label={n.label}
            className="tpm-dropdown"
            data-state={isClosing ? "closing" : "open"}
            style={{
              position: "absolute", top: "calc(100% + 6px)", left: 0, minWidth: 200,
              background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10,
              boxShadow: "0 8px 24px -8px rgba(39,92,115,0.22), 0 2px 6px -2px rgba(0,0,0,0.08)",
              padding: 6, zIndex: 60,
            }}
          >
            {n.children.map((c, i) => {
              const childActive = active === c.route && (!c.tab || true);
              return (
                <button
                  key={c.label}
                  type="button"
                  role="menuitem"
                  className="tpm-item"
                  onClick={() => navigate(c)}
                  style={{
                    display: "flex", alignItems: "center", width: "100%",
                    padding: "8px 16px", borderRadius: 6, border: "none",
                    background: "transparent", cursor: "pointer", textAlign: "left",
                    fontFamily: FONT, fontSize: 13, fontWeight: 400, color: C.itemText,
                    animationDelay: `${i * 25}ms`,
                  }}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ── compact: inline "Pages ▾" hamburger opening a stacked dropdown ─────────
  // Used on tablet (≤1024px) and mobile. Renders inline within the header
  // flex row — no standalone bar, border, or shadow of its own.
  if (compact) {
    return (
      <div ref={rootRef} style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
        <button
          type="button"
          aria-label="Open pages menu"
          aria-haspopup="true"
          aria-expanded={mobileOpen}
          className="tpm-trigger"
          data-no-vella-ring="true"
          onClick={() => setMobileOpen((o) => !o)}
          style={{ ...triggerBase, color: C.inactive, fontWeight: 500 }}
        >
          <MenuIcon size={18} strokeWidth={1.5} />
          <span>Pages</span>
          <ChevronDown size={14} strokeWidth={1.5} className="tpm-chevron" data-open={mobileOpen ? "true" : "false"} />
        </button>
        {mobileOpen && (
          <div
            role="menu"
            aria-label="Pages"
            className="tpm-dropdown"
            data-state="open"
            style={{
              position: "absolute", top: "calc(100% + 6px)", left: 0, minWidth: 220,
              background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10,
              boxShadow: "0 10px 28px -10px rgba(39,92,115,0.28)", padding: 6, zIndex: 60,
            }}
          >
            {NAV_ITEMS.map((n, gi) => (
              <div key={n.key}>
                {!n.children ? (
                  <button
                    type="button" role="menuitem" className="tpm-item"
                    onClick={() => navigate(n)}
                    style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 14px", borderRadius: 6, border: "none", background: activeKey === n.key ? C.pillBg : "transparent", cursor: "pointer", textAlign: "left", fontFamily: FONT, fontSize: 13, fontWeight: activeKey === n.key ? 500 : 400, color: activeKey === n.key ? C.active : C.itemText, animationDelay: `${gi * 25}ms` }}
                  >
                    <n.Icon size={16} strokeWidth={1.5} />{n.label}
                  </button>
                ) : (
                  <div style={{ padding: "6px 0" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 14px", fontFamily: FONT, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "#8a9295" }}>
                      <n.Icon size={14} strokeWidth={1.5} />{n.label}
                    </div>
                    {n.children.map((c) => (
                      <button
                        key={c.label} type="button" role="menuitem" className="tpm-item"
                        onClick={() => navigate(c)}
                        style={{ display: "flex", alignItems: "center", width: "100%", padding: "9px 14px 9px 30px", borderRadius: 6, border: "none", background: "transparent", cursor: "pointer", textAlign: "left", fontFamily: FONT, fontSize: 13, fontWeight: 400, color: C.itemText }}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── desktop: inline horizontal menubar (sits within the header flex row) ───
  return (
    <div
      ref={rootRef}
      role="menubar"
      aria-label="Pages"
      style={{ display: "flex", alignItems: "center", gap: 2, position: "relative" }}
    >
      {NAV_ITEMS.map(renderTopItem)}
    </div>
  );
}
